import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Home, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * Cookie政策页面
 */
function CookiePolicy() {
  const { language } = useLanguage();

  const content = {
    zh: {
      title: 'Cookie 政策',
      lastUpdated: '最后更新：2025年12月23日',
      intro: '本Cookie政策说明DevToolkit如何以及为何使用Cookie和类似技术。我们致力于保护您的隐私，并确保您了解我们如何使用Cookie。',
      sections: [
        {
          title: '1. 什么是 Cookie？',
          content: `
            <p>Cookie 是当您访问网站时存储在您设备上的小型文本文件。Cookie 广泛用于使网站正常工作或更高效地工作，以及向网站所有者提供信息。</p>
            <h3>Cookie 的类型</h3>
            <ul>
              <li><strong>会话 Cookie：</strong>临时 Cookie，在您关闭浏览器时会被删除</li>
              <li><strong>持久性 Cookie：</strong>在设定的时间段内保留在您的设备上</li>
              <li><strong>第一方 Cookie：</strong>由我们的网站直接设置</li>
              <li><strong>第三方 Cookie：</strong>由第三方服务（如 Google AdSense）设置</li>
            </ul>
          `
        },
        {
          title: '2. 我们使用的 Cookie',
          content: `
            <p>我们使用以下类别的 Cookie：</p>

            <h3>2.1 必要 Cookie</h3>
            <p>这些 Cookie 对于网站的基本功能至关重要。</p>
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">Cookie 名称</th>
                  <th class="text-left">用途</th>
                  <th class="text-left">有效期</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>theme</code></td>
                  <td>保存用户的主题偏好（亮色/暗色）</td>
                  <td>1年</td>
                </tr>
                <tr>
                  <td><code>language</code></td>
                  <td>保存用户的语言选择</td>
                  <td>1年</td>
                </tr>
                <tr>
                  <td><code>localStorage</code></td>
                  <td>保存 JSON 输入历史记录（仅本地）</td>
                  <td>永久（直到手动清除）</td>
                </tr>
              </tbody>
            </table>

            <h3>2.2 分析 Cookie（Google Analytics）</h3>
            <p>这些 Cookie 帮助我们了解访问者如何使用我们的网站。</p>
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">Cookie 名称</th>
                  <th class="text-left">用途</th>
                  <th class="text-left">有效期</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>_ga</code></td>
                  <td>用于区分用户</td>
                  <td>2年</td>
                </tr>
                <tr>
                  <td><code>_ga_*</code></td>
                  <td>用于保持会话状态</td>
                  <td>2年</td>
                </tr>
                <tr>
                  <td><code>_gid</code></td>
                  <td>用于区分用户</td>
                  <td>24小时</td>
                </tr>
              </tbody>
            </table>
            <p><strong>提供商：</strong>Google LLC</p>
            <p><strong>详细了解：</strong><a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer">Google Analytics Cookie 使用情况</a></p>

            <h3>2.3 广告 Cookie（Google AdSense）</h3>
            <p>这些第三方 Cookie 用于投放个性化广告。</p>
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">Cookie 名称</th>
                  <th class="text-left">用途</th>
                  <th class="text-left">有效期</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>__gads</code></td>
                  <td>用于根据用户的兴趣投放广告</td>
                  <td>13个月</td>
                </tr>
                <tr>
                  <td><code>__gpi</code></td>
                  <td>用于收集有关网站访问的信息</td>
                  <td>13个月</td>
                </tr>
                <tr>
                  <td><code>DSID</code></td>
                  <td>用于识别已登录用户</td>
                  <td>2周</td>
                </tr>
                <tr>
                  <td><code>IDE</code></td>
                  <td>用于衡量广告的效果</td>
                  <td>13个月</td>
                </tr>
              </tbody>
            </table>
            <p><strong>提供商：</strong>Google LLC</p>
            <p><strong>用途说明：</strong></p>
            <ul>
              <li>Google 及其合作伙伴使用这些 Cookie 在您的浏览器中放置和读取 Cookie</li>
              <li>根据您过去对本网站和其他网站的访问记录来投放广告</li>
              <li>衡量广告的有效性和覆盖范围</li>
              <li>防止广告欺诈</li>
            </ul>
            <p><strong>详细了解：</strong></p>
            <ul>
              <li><a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google 如何使用广告 Cookie</a></li>
              <li><a href="https://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener noreferrer">Google 如何使用合作伙伴网站的数据</a></li>
            </ul>
          `
        },
        {
          title: '3. 如何管理 Cookie',
          content: `
            <p>您有权选择是否接受 Cookie。以下是管理 Cookie 的几种方法：</p>

            <h3>3.1 浏览器设置</h3>
            <p>大多数浏览器允许您：</p>
            <ul>
              <li>查看您设备上存储的 Cookie</li>
              <li>删除部分或全部 Cookie</li>
              <li>阻止第三方 Cookie</li>
              <li>关闭浏览器时删除所有 Cookie</li>
              <li>激活"禁止跟踪"功能</li>
            </ul>
            <p><strong>主流浏览器的 Cookie 设置：</strong></p>
            <ul>
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a></li>
              <li><a href="https://support.mozilla.org/zh-CN/kb/cookies" target="_blank" rel="noopener noreferrer">Firefox</a></li>
              <li><a href="https://support.apple.com/zh-cn/HT201265" target="_blank" rel="noopener noreferrer">Safari</a></li>
              <li><a href="https://support.microsoft.com/zh-cn/microsoft-edge" target="_blank" rel="noopener noreferrer">Edge</a></li>
            </ul>

            <h3>3.2 退出 Google 个性化广告</h3>
            <ul>
              <li><strong>Google 广告设置：</strong>访问 <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google 广告设置</a> 停用个性化广告</li>
              <li><strong>Google Analytics 退出：</strong>安装 <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics 退出浏览器插件</a></li>
            </ul>

            <h3>3.3 行业选择退出工具</h3>
            <p>您还可以使用以下工具退出多个广告网络的 Cookie：</p>
            <ul>
              <li><a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance (美国)</a></li>
              <li><a href="http://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer">Your Online Choices (欧盟)</a></li>
              <li><a href="http://youradchoices.ca/" target="_blank" rel="noopener noreferrer">Ad Choices (加拿大)</a></li>
            </ul>

            <h3>3.4 禁用 Cookie 的影响</h3>
            <p>请注意，禁用某些 Cookie 可能会影响网站功能：</p>
            <ul>
              <li>❌ 无法保存主题和语言偏好</li>
              <li>❌ 无法保存 JSON 输入历史记录</li>
              <li>❌ 某些功能可能无法正常工作</li>
              <li>✅ 您仍然可以使用网站的核心功能</li>
            </ul>
          `
        },
        {
          title: '4. 同意和撤回同意',
          content: `
            <p>通过继续使用我们的网站，您同意我们按照本政策使用 Cookie。</p>
            <h3>如何撤回同意</h3>
            <ul>
              <li>您可以随时通过浏览器设置删除 Cookie</li>
              <li>您可以通过 Google 广告设置停用个性化广告</li>
              <li>您可以使用行业选择退出工具退出广告 Cookie</li>
            </ul>
          `
        },
        {
          title: '5. Cookie 政策更新',
          content: `
            <p>我们可能会不时更新本 Cookie 政策，以反映我们的做法或法律要求的变化。</p>
            <ul>
              <li>重大变更时，我们会更新本页面顶部的"最后更新"日期</li>
              <li>建议您定期查看本政策</li>
            </ul>
          `
        },
        {
          title: '6. 联系我们',
          content: `
            <p>如果您对本 Cookie 政策或我们的隐私做法有任何疑问，请联系我们：</p>
            <ul>
              <li><strong>GitHub：</strong><a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">提交 Issue</a></li>
              <li><strong>网站：</strong><a href="/contact">联系页面</a></li>
            </ul>
          `
        }
      ]
    },
    en: {
      title: 'Cookie Policy',
      lastUpdated: 'Last Updated: December 23, 2025',
      intro: 'This Cookie Policy explains how and why DevToolkit uses cookies and similar technologies. We are committed to protecting your privacy and ensuring you understand how we use cookies.',
      sections: [
        {
          title: '1. What Are Cookies?',
          content: `
            <p>Cookies are small text files stored on your device when you visit a website. Cookies are widely used to make websites work or work more efficiently, as well as to provide information to website owners.</p>
            <h3>Types of Cookies</h3>
            <ul>
              <li><strong>Session Cookies:</strong> Temporary cookies that are deleted when you close your browser</li>
              <li><strong>Persistent Cookies:</strong> Remain on your device for a set period</li>
              <li><strong>First-Party Cookies:</strong> Set directly by our website</li>
              <li><strong>Third-Party Cookies:</strong> Set by third-party services (such as Google AdSense)</li>
            </ul>
          `
        },
        {
          title: '2. Cookies We Use',
          content: `
            <p>We use the following categories of cookies:</p>

            <h3>2.1 Necessary Cookies</h3>
            <p>These cookies are essential for the basic functionality of the website.</p>
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">Cookie Name</th>
                  <th class="text-left">Purpose</th>
                  <th class="text-left">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>theme</code></td>
                  <td>Save user theme preference (light/dark)</td>
                  <td>1 year</td>
                </tr>
                <tr>
                  <td><code>language</code></td>
                  <td>Save user language choice</td>
                  <td>1 year</td>
                </tr>
                <tr>
                  <td><code>localStorage</code></td>
                  <td>Save JSON input history (local only)</td>
                  <td>Permanent (until manually cleared)</td>
                </tr>
              </tbody>
            </table>

            <h3>2.2 Analytics Cookies (Google Analytics)</h3>
            <p>These cookies help us understand how visitors use our website.</p>
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">Cookie Name</th>
                  <th class="text-left">Purpose</th>
                  <th class="text-left">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>_ga</code></td>
                  <td>Used to distinguish users</td>
                  <td>2 years</td>
                </tr>
                <tr>
                  <td><code>_ga_*</code></td>
                  <td>Used to maintain session state</td>
                  <td>2 years</td>
                </tr>
                <tr>
                  <td><code>_gid</code></td>
                  <td>Used to distinguish users</td>
                  <td>24 hours</td>
                </tr>
              </tbody>
            </table>
            <p><strong>Provider:</strong> Google LLC</p>
            <p><strong>Learn more:</strong> <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer">Google Analytics Cookie Usage</a></p>

            <h3>2.3 Advertising Cookies (Google AdSense)</h3>
            <p>These third-party cookies are used to serve personalized advertisements.</p>
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">Cookie Name</th>
                  <th class="text-left">Purpose</th>
                  <th class="text-left">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>__gads</code></td>
                  <td>Used to serve ads based on user interests</td>
                  <td>13 months</td>
                </tr>
                <tr>
                  <td><code>__gpi</code></td>
                  <td>Used to collect information about website visits</td>
                  <td>13 months</td>
                </tr>
                <tr>
                  <td><code>DSID</code></td>
                  <td>Used to identify logged-in users</td>
                  <td>2 weeks</td>
                </tr>
                <tr>
                  <td><code>IDE</code></td>
                  <td>Used to measure ad effectiveness</td>
                  <td>13 months</td>
                </tr>
              </tbody>
            </table>
            <p><strong>Provider:</strong> Google LLC</p>
            <p><strong>Purpose Details:</strong></p>
            <ul>
              <li>Google and its partners use these cookies to place and read cookies in your browser</li>
              <li>Serve ads based on your past visits to this and other websites</li>
              <li>Measure ad effectiveness and reach</li>
              <li>Prevent ad fraud</li>
            </ul>
            <p><strong>Learn more:</strong></p>
            <ul>
              <li><a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">How Google uses advertising cookies</a></li>
              <li><a href="https://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener noreferrer">How Google uses data from partner sites</a></li>
            </ul>
          `
        },
        {
          title: '3. How to Manage Cookies',
          content: `
            <p>You have the right to choose whether to accept cookies. Here are several ways to manage cookies:</p>

            <h3>3.1 Browser Settings</h3>
            <p>Most browsers allow you to:</p>
            <ul>
              <li>View cookies stored on your device</li>
              <li>Delete some or all cookies</li>
              <li>Block third-party cookies</li>
              <li>Delete all cookies when closing the browser</li>
              <li>Activate "Do Not Track" feature</li>
            </ul>
            <p><strong>Cookie settings for popular browsers:</strong></p>
            <ul>
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471" target="_blank" rel="noopener noreferrer">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge" target="_blank" rel="noopener noreferrer">Edge</a></li>
            </ul>

            <h3>3.2 Opt Out of Google Personalized Ads</h3>
            <ul>
              <li><strong>Google Ads Settings:</strong> Visit <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a> to opt out of personalized advertising</li>
              <li><strong>Google Analytics Opt-out:</strong> Install <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a></li>
            </ul>

            <h3>3.3 Industry Opt-out Tools</h3>
            <p>You can also use the following tools to opt out of cookies from multiple ad networks:</p>
            <ul>
              <li><a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance (US)</a></li>
              <li><a href="http://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer">Your Online Choices (EU)</a></li>
              <li><a href="http://youradchoices.ca/" target="_blank" rel="noopener noreferrer">Ad Choices (Canada)</a></li>
            </ul>

            <h3>3.4 Impact of Disabling Cookies</h3>
            <p>Please note that disabling certain cookies may affect website functionality:</p>
            <ul>
              <li>❌ Cannot save theme and language preferences</li>
              <li>❌ Cannot save JSON input history</li>
              <li>❌ Some features may not work properly</li>
              <li>✅ You can still use core website features</li>
            </ul>
          `
        },
        {
          title: '4. Consent and Withdrawal',
          content: `
            <p>By continuing to use our website, you consent to our use of cookies as described in this policy.</p>
            <h3>How to Withdraw Consent</h3>
            <ul>
              <li>You can delete cookies at any time through your browser settings</li>
              <li>You can opt out of personalized advertising through Google Ads Settings</li>
              <li>You can use industry opt-out tools to opt out of advertising cookies</li>
            </ul>
          `
        },
        {
          title: '5. Cookie Policy Updates',
          content: `
            <p>We may update this Cookie Policy from time to time to reflect changes in our practices or legal requirements.</p>
            <ul>
              <li>For major changes, we will update the "Last Updated" date at the top of this page</li>
              <li>We recommend reviewing this policy regularly</li>
            </ul>
          `
        },
        {
          title: '6. Contact Us',
          content: `
            <p>If you have any questions about this Cookie Policy or our privacy practices, please contact us:</p>
            <ul>
              <li><strong>GitHub:</strong> <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">Submit an Issue</a></li>
              <li><strong>Website:</strong> <a href="/contact">Contact Page</a></li>
            </ul>
          `
        }
      ]
    }
  };

  const data = content[language];

  return (
    <div className="min-h-screen bg-[#faf9f6] dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 dark:from-amber-600 dark:to-orange-700 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-4">
            <Cookie size={32} />
            <h1 className="text-4xl font-bold">{data.title}</h1>
          </div>
          <p className="text-lg text-amber-100">{data.lastUpdated}</p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Link to="/" className="hover:text-amber-600 dark:hover:text-amber-400 flex items-center gap-1">
              <Home size={14} />
              {language === 'zh' ? '首页' : 'Home'}
            </Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">{data.title}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* Intro */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-6 mb-12">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{data.intro}</p>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none
          prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700
          prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
          prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-amber-600 dark:prose-a:text-amber-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
          prose-ul:my-4 prose-li:my-1
          prose-table:border-collapse prose-table:w-full
          prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:p-2
          prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:p-2
          prose-code:text-amber-600 dark:prose-code:text-amber-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
        ">
          {data.sections.map((section, index) => (
            <section key={index}>
              <h2>{section.title}</h2>
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </section>
          ))}
        </div>

        {/* Back Button */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </article>
    </div>
  );
}

export default CookiePolicy;
